import { APP_INITIALIZER, inject, NgModule, Provider } from '@angular/core';
import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import {
    LOCAL_STORAGE_CONFIG,
    httpBusyStateProviders,
    provideHttpBusyStateIgnoredUrl,
    SessionFacade,
    SharedCoreModule,
    POLLING_DEFAULT_CONFIG,
    TimerParams,
    AppInitializeService,
    SessionRepository
} from '@frontend-site/shared/core';
import { hybridMessagePayloadBusyStateProvider, HybridModule } from '@frontend-site/shared/hybrid';
import { API_URL } from '@frontend-site/shared/data';
import { routerStoreBusyStateProvider } from '@frontend-site/shared/router-store';
import { projectDetailsCleanerServiceProvider } from '@frontend-site/project/domain';
import { systemDetailsCleanerServiceProvider } from '@frontend-site/system/domain';

import {
    PLoadingModule,
    provideDefaultEmptyStateVisualSource,
    PDialogService,
    PFeedbackDialogComponent
} from '@frontend-site/ui/components';
import { AuthDomainModule, PUBLIC_ROUTES } from '@frontend-site/auth/domain';
import { MainHeaderModule } from '@frontend-site/main-header/main-header';
import { ProjectFeaturesModule } from '@frontend-site/project/features';
import { providePAiFeedback } from '@panaya/ui/p-ai-feedback';
import { AiFeedbackService } from '@frontend-site/ai/domain';

import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { appInit } from './app-init';
import { routes } from './routes';
import { LicenseManager } from '@ag-grid-enterprise/core';
import { ModuleRegistry } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { MasterDetailModule } from '@ag-grid-enterprise/master-detail';
import { menuItemRefresherProviders } from './menu-item-refresher-init';
import { seemoreAttachmentSourceProviders } from './seemore/attachment-sources-init';
import { COMPANY_ID_TOKEN } from '@modules/settings/security/api-tokens/api-token.resolver';
import { sentenceCaseDictionaryProvider } from './config/sentence-case-dictionary';
import { MarkdownModule } from 'ngx-markdown';
import { provideLottieOptions } from 'ngx-lottie';
import { UploadProgressComponent } from '@modules/shared/ui/uploadprogress/upload-progress.component';
import { PSplitterModule } from '@panaya/ui/p-splitter';
import { AiChatPanelComponent } from '@panaya/ai-chat/features';
import { AiChatApiService } from '@panaya/ai-chat/domain';
import { AiChatRouterService } from '@modules/ai-chat/ai-chat-router.service';

ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    ExcelExportModule,
    ServerSideRowModelModule,
    RowGroupingModule,
    SetFilterModule,
    MasterDetailModule
]);

LicenseManager.setLicenseKey(
    `CompanyName=Panaya Inc,LicensedGroup=R&D,LicenseType=MultipleApplications,LicensedConcurrentDeveloperCount=18,LicensedProductionInstancesCount=3,AssetReference=AG-016801,ExpiryDate=15_July_2022_[v2]_MTY1NzgzOTYwMDAwMA==1ff6be4e047c9b046ce462a8e4b918d8`
);

export function HttpLoaderFactory(httpClient: HttpClient): TranslateHttpLoader {
    return new TranslateHttpLoader(httpClient, environment.baseUrl + 'assets/i18n_a/locale-');
}

/**
 * Hooks heavy lifting mechanisms to the `LoadingStateService`.
 * Add providers here to hook a busy state reporting service to the global blocking loading spinner.
 */
// eslint-disable-next-line prettier/prettier
const busyStateProviders: Provider[] = [hybridMessagePayloadBusyStateProvider, routerStoreBusyStateProvider, httpBusyStateProviders];

/** Hooks the urls to be ignored by the http busy state interceptor. */
// eslint-disable-next-line prettier/prettier
const busyStateIgnoredHttpRequestUrlProviders: Provider[] = [
    'rest/common/progressStatus',
    'rest/rdx/searchEntityIds',
    'rest/sap-data/sci/test-case',
    'rest/acc/submitSyncAccFromList',
    'rest/acc/submitSyncAccFromPopup',
    'rest/acc/getSyncAccStatus',
    'rest/notifications/userNotifications',
    'rest/executiontree/read',
    'rest/tmCommon/longRequests',
    'rest/tmCommon/myMastheadCounts',
    'rest/common/progressStatus',
    'rest/tmCommon/myMastheadCounts',
    '/rest/changes/linkTransportsToRequirement',
    'assets/icons/svgs',
    '/rest/salesforce/assets/getExplanation',
    '/rest/salesforce/assets/getExplanationHashCode',
    '/rest/corrections/saveNoteSummaryFeedback',
    '/rest/execution/et/isPlayerRunFinished',
    '/rest/pa/isScriptUpdated',
    '/rest/execution/et/isAdditionalRunAdded/',
    // 'rest/pa/execution/details',
    '/rest/execution/steps/grid/columnsState',
    '/rest/salesforce/apiTests/executeTest',
    '/rest/llm/chat/init_thread',
    '/rest/llm/chat/suggestions',
    '/rest/llm/chat/llm_response',
    '/rest/llm/chat/start_fetch_llm_response',
    '/rest/llm/chat/user_feedback',
    '/rest/llm/chat/interrupt',
    'rest/execution/generateComponentDescription',
    '/rest/automatedTestMigration/migrate'
].map(provideHttpBusyStateIgnoredUrl);

/**
 * Hooks route change event 'interceptor' handlers`.
 * Add providers here to hook a route change handler service to the global route change stream.
 */
// eslint-disable-next-line prettier/prettier
const appScopeServiceProviders: Provider[] = [projectDetailsCleanerServiceProvider, systemDetailsCleanerServiceProvider];

function getIframeSrc(): string {
    return environment.iframeSrc + location.pathname.replace('/site/rdx', '') + location.search;
}

@NgModule({
    declarations: [AppComponent],
    providers: [
        { provide: APP_BASE_HREF, useValue: environment.baseUrl },
        { provide: API_URL, useValue: environment.apiUrl },
        {
            provide: APP_INITIALIZER,
            useFactory: appInit,
            multi: true,
            deps: [DOCUMENT, SessionFacade, AppInitializeService, HttpClient]
        },
        { provide: COMPANY_ID_TOKEN, useFactory: (): number => inject(SessionRepository).applicationConfig.companyId },
        { provide: PUBLIC_ROUTES, useValue: ['/assets'] },
        { provide: LOCAL_STORAGE_CONFIG, useValue: { prefix: 'pqm' } },
        { provide: POLLING_DEFAULT_CONFIG, useValue: { startDue: 0, intervalMS: 300000 } as TimerParams },
        sentenceCaseDictionaryProvider,
        menuItemRefresherProviders,
        seemoreAttachmentSourceProviders,
        busyStateProviders,
        busyStateIgnoredHttpRequestUrlProviders,
        appScopeServiceProviders,
        provideLottieOptions({ player: () => import('lottie-web') }),
        provideDefaultEmptyStateVisualSource('seemore-boxes-animation'),
        providePAiFeedback({
            dialogService: PDialogService,
            feedbackDialogComponent: PFeedbackDialogComponent,
            feedbackService: AiFeedbackService
        }),
        { provide: AiChatApiService, useExisting: AiChatRouterService }
    ],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        SharedCoreModule,
        AuthDomainModule,
        HybridModule.config({
            initialIframeSourceUrl: getIframeSrc()
        }),
        MainHeaderModule,
        TranslateModule.forRoot({
            defaultLanguage: 'en_US',
            extend: true,
            loader: {
                provide: TranslateLoader,
                useFactory: (httpClient: HttpClient) => new TranslateHttpLoader(httpClient, '/assets/i18n_a/locale-'),
                deps: [HttpClient]
            }
        }),
        MatDialogModule,
        MatSnackBarModule,
        ClipboardModule,
        ProjectFeaturesModule,
        RouterModule.forRoot(routes, { initialNavigation: 'enabledNonBlocking', paramsInheritanceStrategy: 'always' }),
        PLoadingModule,
        MarkdownModule.forRoot(),
        UploadProgressComponent,
        PSplitterModule,
        AiChatPanelComponent
    ]
})
export class AppModule {}
